// Settings → Tax. Owner-configurable tax:
//   • named parts per location, plus business-wide default parts
//   • taxability per charge type (rental / sale / delivery / damage / late fee)
//   • "this business charges no sales tax" confirmation
// Backend: routes/rental-tax.js. Nothing here computes tax — every order
// and quote is totalled server-side (backend lib/totals.js) and stores
// its own snapshot, so editing a part never changes a past order.
import React, { useState, useEffect, useCallback } from 'react';
import axios from './lib/axiosClient';

const CHARGE_LABELS = {
  rental:   ['Rental charges', 'Day / week / month equipment rental lines'],
  sale:     ['Sales', 'Products sold outright (product type "sell", or overridden as "taxed as sale")'],
  delivery: ['Delivery fee', 'Mileage / delivery charge'],
  damage:   ['Damage charges', 'Damage assessed at return'],
  late_fee: ['Late fees', 'Overdue return charges'],
};

export default function TaxSettingsTab({ C, card, sec, lbl, inp, locations }) {
  const [parts, setParts]   = useState([]);
  const [rules, setRules]   = useState([]);
  const [flags, setFlags]   = useState({ no_sales_tax: false });
  const [err, setErr]       = useState('');
  const [newPart, setNewPart] = useState({}); // keyed by scope ('default' | location id)
  const [editing, setEditing] = useState(null); // { id, name, rate }

  const load = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([axios.get('/api/rentals/tax/parts'), axios.get('/api/rentals/tax/rules')]);
      setParts(p.data.parts || []);
      setRules(r.data.rules || []);
      setFlags({ no_sales_tax: !!r.data.no_sales_tax });
      setErr('');
    } catch (e) { setErr(e.response?.data?.error || 'Could not load tax settings'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const scopes = [{ id: null, key: 'default', name: 'Business default', hint: 'Used by any location that has no parts of its own' },
    ...(locations || []).map(l => ({ id: l.id, key: String(l.id), name: l.name, hint: l.is_default ? 'Default location' : null }))];
  const partsFor = (id) => parts.filter(p => (id === null ? p.location_id === null : p.location_id === id));
  const combined = (list) => Math.round(list.reduce((s, p) => s + Number(p.rate), 0) * 10000) / 10000;
  const hasAnyParts = parts.length > 0;

  async function addPart(scope) {
    const draft = newPart[scope.key] || {};
    if (!draft.name?.trim() || draft.rate === undefined || draft.rate === '') return;
    try {
      await axios.post('/api/rentals/tax/parts', { location_id: scope.id, name: draft.name.trim(), rate: Number(draft.rate), sort_order: partsFor(scope.id).length });
      setNewPart(p => ({ ...p, [scope.key]: {} }));
      await load();
    } catch (e) { setErr(e.response?.data?.error || 'Could not add part'); }
  }
  async function savePart() {
    if (!editing) return;
    try {
      await axios.put(`/api/rentals/tax/parts/${editing.id}`, { name: editing.name, rate: Number(editing.rate) });
      setEditing(null); await load();
    } catch (e) { setErr(e.response?.data?.error || 'Could not save part'); }
  }
  async function deletePart(p) {
    if (!window.confirm(`Delete "${p.name}" (${p.rate}%)? Past orders keep the rate they were charged.`)) return;
    try { await axios.delete(`/api/rentals/tax/parts/${p.id}`); await load(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not delete part'); }
  }
  async function toggleRule(ct, taxable) {
    try { const r = await axios.put('/api/rentals/tax/rules', { rules: { [ct]: taxable } }); setRules(r.data.rules || []); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save'); }
  }
  async function markReviewed(ct) {
    try { const r = await axios.post(`/api/rentals/tax/rules/${ct}/reviewed`); setRules(r.data.rules || []); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save'); }
  }
  async function setNoSalesTax(v) {
    try { const r = await axios.put('/api/rentals/tax/rules', { no_sales_tax: v }); setFlags({ no_sales_tax: !!r.data.no_sales_tax }); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save'); }
  }

  const small = { padding:'5px 10px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.blue, fontSize:11, cursor:'pointer' };

  return (
    <div>
      {err && <div style={{ padding:'10px 14px', background:'#3b0a0a', border:'1px solid #7f1d1d', borderRadius:8, color:'#fca5a5', fontSize:13, marginBottom:14 }}>{err}</div>}

      {!hasAnyParts && !flags.no_sales_tax && (
        <div data-testid="tax-warning" style={{ padding:'12px 16px', background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.35)', borderRadius:8, color:'#fbbf24', fontSize:13, marginBottom:16, lineHeight:1.5 }}>
          ⚠️ <strong>No tax configured.</strong> Orders and quotes are being created with $0 tax. Add at least one tax part below, or confirm that this business charges no sales tax.
        </div>
      )}

      <div style={card}>
        <div style={sec}>🧾 Tax parts</div>
        <div style={{ fontSize:12, color:C.muted, marginBottom:14, lineHeight:1.5 }}>
          Tax is charged as named parts (for example State, County, City) added together. An order uses the parts of its location; a location with no parts uses the business default. Every order and quote stores the parts and rates it was charged, so changing a part here never alters a past order.
        </div>
        {scopes.map(scope => {
          const list = partsFor(scope.id);
          const draft = newPart[scope.key] || {};
          return (
            <div key={scope.key} style={{ padding:'12px 0', borderBottom:`1px solid ${C.border}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{scope.name}</div>
                {scope.hint && <span style={{ fontSize:10, color:C.muted }}>{scope.hint}</span>}
                <div style={{ marginLeft:'auto', fontSize:12, fontWeight:700, color: list.length ? C.amber : C.muted }}>
                  {list.length ? `${combined(list)}% combined` : (scope.id === null ? 'no default parts' : 'uses business default')}
                </div>
              </div>
              {list.map(p => (
                <div key={p.id} data-testid="tax-part-row" style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0 6px 12px', fontSize:13 }}>
                  {editing?.id === p.id ? (
                    <>
                      <input style={{ ...inp, width:220 }} value={editing.name} onChange={e => setEditing(x => ({ ...x, name: e.target.value }))} />
                      <input style={{ ...inp, width:100 }} type="number" min="0" max="100" step="0.0001" value={editing.rate} onChange={e => setEditing(x => ({ ...x, rate: e.target.value }))} />
                      <span style={{ color:C.muted }}>%</span>
                      <button onClick={savePart} style={{ ...small, color:'#fff', background:C.accent, border:'none', fontWeight:700 }}>Save</button>
                      <button onClick={() => setEditing(null)} style={small}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <span style={{ color:C.text, flex:1 }}>{p.name}</span>
                      <span style={{ color:C.amber, fontWeight:700, width:90, textAlign:'right' }}>{Number(p.rate)}%</span>
                      <button onClick={() => setEditing({ id: p.id, name: p.name, rate: p.rate })} style={small}>Edit</button>
                      <button onClick={() => deletePart(p)} style={{ ...small, color:C.red, borderColor:'#3b0a0a' }}>Delete</button>
                    </>
                  )}
                </div>
              ))}
              <div style={{ display:'flex', gap:8, alignItems:'center', paddingLeft:12, marginTop:6 }}>
                <input style={{ ...inp, width:220 }} placeholder="Part name (e.g. State sales tax)" value={draft.name || ''} onChange={e => setNewPart(x => ({ ...x, [scope.key]: { ...draft, name: e.target.value } }))} />
                <input style={{ ...inp, width:100 }} type="number" min="0" max="100" step="0.0001" placeholder="Rate" value={draft.rate ?? ''} onChange={e => setNewPart(x => ({ ...x, [scope.key]: { ...draft, rate: e.target.value } }))} />
                <span style={{ color:C.muted, fontSize:12 }}>%</span>
                <button onClick={() => addPart(scope)} disabled={!draft.name?.trim() || draft.rate === undefined || draft.rate === ''}
                  style={{ padding:'7px 12px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', opacity: (!draft.name?.trim() || draft.rate === undefined || draft.rate === '') ? 0.5 : 1 }}>+ Add part</button>
              </div>
            </div>
          );
        })}
        <label style={{ display:'flex', alignItems:'flex-start', gap:8, marginTop:14, fontSize:12, color:C.muted, cursor:'pointer', lineHeight:1.5 }}>
          <input type="checkbox" checked={flags.no_sales_tax} onChange={e => setNoSalesTax(e.target.checked)} style={{ accentColor:C.accent, marginTop:3 }} />
          <span><strong style={{ color:C.text }}>This business charges no sales tax.</strong> Confirms that $0 tax is intentional and removes the warning from the order and quote screens. Parts added above still apply.</span>
        </label>
      </div>

      <div style={card}>
        <div style={sec}>✅ What is taxable</div>
        <div style={{ fontSize:12, color:C.muted, marginBottom:12 }}>Per charge type. Booking and security deposits are never taxed. A product can override this (Products → edit → Tax).</div>
        {rules.map(r => {
          const [label, hint] = CHARGE_LABELS[r.charge_type] || [r.charge_type, ''];
          return (
            <div key={r.charge_type} data-testid={`tax-rule-${r.charge_type}`} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', flex:1 }}>
                <input type="checkbox" checked={r.taxable} onChange={e => toggleRule(r.charge_type, e.target.checked)} style={{ accentColor:C.accent }} />
                <span>
                  <span style={{ fontSize:13, fontWeight:700, color:C.text }}>{label}</span>
                  <span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>{hint}</span>
                </span>
              </label>
              <span style={{ fontSize:11, fontWeight:700, color: r.taxable ? C.green : C.muted }}>{r.taxable ? 'TAXABLE' : 'not taxed'}</span>
              {r.needs_review && (
                <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:10, padding:'2px 8px', borderRadius:4, background:'rgba(251,191,36,0.12)', color:'#fbbf24', fontWeight:700 }}>NEEDS ACCOUNTANT REVIEW</span>
                  <button onClick={() => markReviewed(r.charge_type)} style={small}>Mark reviewed</button>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
